import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageContainer } from '../../../modules/page-container/page-container';
import { useCartQuery } from '../../../modules/cart/queries';
import { GridContainer, GridItem } from '../../../visual-components/grid/grid';
import { CartTable } from '../../../modules/cart/components/cart-table';
import { useCustomerQuery } from '../../../modules/customer/queries';
import { Stepper } from '../../../visual-components/stepper/stepper';
import { Spacer } from '../../../visual-components/spacer/spacer';
import { InlineNotification } from '../../../visual-components/inline-notification/inline-notification';
import { CartDetails } from '../../../modules/cart/components/cart-details';
import { ProcessButtonLayout } from '../../../visual-components/process-button-layout/process-button-layout';
import { Button, ButtonLink } from '../../../visual-components/button/button';
import { usePathBuilder } from '../../../core/routing/hooks/use-path-builder';
import { Checkbox } from '../../../visual-components/checkbox/checkbox';
import { Link } from '../../../visual-components/link/link';
import { usePlaceOrder } from '../../../modules/cart/mutations';
import { Input } from '../../../visual-components/input/input';
import { useDeliveryAddress } from '../../../modules/cart/components/hooks/use-delivery-address';
import { TextArea } from '../../../visual-components/text-area/text-area';
import { TableCellDeleteItem } from '../../../modules/cart/components/table-cell-delete-item';

export const Overview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cartPath, homePath } = usePathBuilder();
  const { data: cart, isLoading: isLoadingCart, error: cartError } = useCartQuery();

  const { mutateAsync: placeOrder, isPending: isPendingPlaceOrder } = usePlaceOrder({
    onSuccess: () => {
      setOrdered(true);
      window.scrollTo(0, 0);
    },
  });

  const [orderReference, setOrderReference] = useState('');
  const [comment, setComment] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [ordered, setOrdered] = useState(false);

  const { data: customer, isLoading: isLoadingCustomer, error: customerError } = useCustomerQuery();
  const customerData = customer?.data;
  const { selectedDeliveryAddress } = useDeliveryAddress(customerData?.shippingAddresses);

  // TODO: @Simon reconsider adding deliverAddress selection again (Also note default handling in placeOrder)
  const canContinue = !isLoadingCart && !isLoadingCustomer && (cart?.data?.items.length ?? 0) > 0 && accepted;

  const finalSubmit = () => {
    if (!canContinue) {
      return;
    }

    placeOrder({
      deliveryAddressId: selectedDeliveryAddress?.addressId ?? '',
      orderReference: orderReference.length > 0 ? orderReference : null,
      comment: comment.length > 0 ? comment : null,
    });
  };

  if (ordered) {
    return (
      <PageContainer documentTitle={t('cart.documentTitle')}>
        <GridContainer>
          <GridItem>
            <Stepper
              currentStep={3}
              steps={[
                {
                  label: t('cart.stepper.cart'),
                },
                { label: t('cart.stepper.finalize') },
                {
                  label: t('cart.stepper.confirm'),
                },
              ]}
            />
            <Spacer size="09" />
            <InlineNotification
              actions={<ButtonLink to={homePath()}>{t('global.backToHome')}</ButtonLink>}
              text={t('cart.orderSuccess.text')}
              title={t('cart.orderSuccess.title')}
              type="success"
            />
          </GridItem>
        </GridContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer documentTitle={t('cart.documentTitle')}>
      <GridContainer>
        <GridItem>
          <Stepper
            currentStep={2}
            steps={[
              {
                label: t('cart.stepper.cart'),
                onCompletedClick: () => {
                  navigate(cartPath());
                },
              },
              { label: t('cart.stepper.finalize') },
              {
                label: t('cart.stepper.confirm'),
              },
            ]}
          />
          <Spacer size="09" />
          <InlineNotification
            text={t('cart.preliminaryHint.text')}
            title={t('cart.preliminaryHint.title')}
            type="info"
          />
          <Spacer size="06" />
          <CartTable
            readOnly
            data={cart?.data}
            error={cartError}
            isLoading={isLoadingCart}
            renderDeleteItemCell={({ articleId, container, erpContainerArticleId, weightKg }) => (
              <TableCellDeleteItem
                articleId={articleId}
                container={container}
                erpContainerArticleId={erpContainerArticleId}
                weightKg={weightKg}
              />
            )}
          />
          <Spacer size="08" />
        </GridItem>
        <GridItem span="6">
          <Input
            label={t('cart.orderReference')}
            type="text"
            value={orderReference}
            onChange={(e) => {
              setOrderReference(e.target.value);
            }}
          />
          <TextArea
            label={t('form.id.comment')}
            maxLength={300}
            type="text"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
            }}
          />
          <CartDetails readOnly customerData={customerData} error={customerError} isLoading={isLoadingCustomer} />
        </GridItem>
        <GridItem>
          <Checkbox checked={accepted} onChange={(checked) => setAccepted(checked)}>
            <Trans i18nKey="cart.acceptTerms">
              <Link rel="noopener noreferrer" target="_blank" to={t('global.termsAndConditions.linkTo')} />
            </Trans>
          </Checkbox>
          <Spacer size="06" />
          <ProcessButtonLayout
            left={
              <Link icon="arrow-left" iconPosition="left" to={cartPath()}>
                {t('pagination.back')}
              </Link>
            }
            right={
              <Button
                disabled={!canContinue || isPendingPlaceOrder}
                icon="check"
                iconPosition="right"
                variant="primary"
                onClick={() => {
                  finalSubmit();
                }}
              >
                {t('cart.order')}
              </Button>
            }
          />
        </GridItem>
      </GridContainer>
    </PageContainer>
  );
};
